import {Box, makeStyles, Typography} from '@material-ui/core'
import {city} from 'plateform-project-domain'
import {city as cityStats} from 'plateform-project-stats-domain'
import React, {useContext} from 'react'
import {useTranslation} from 'react-i18next'
import {MenuItemAction, MoreOptions} from '../../Component/MoreOptions'
import {ProgressBar} from '../../Component/ProgressBar'
import {StatusTags, StatusTagsFormProps} from '../../Component/StatusTags'
import {PrimaryTypography, Table} from '../../Component/Table'
import {displayNumber, UTCToTimedZone} from '../../utils'
import {themeContext} from "@smartb/archetypes-ui-components";

export interface Campaign extends city.colisactiv.project.model.Campaign {}
export interface CampaignStats
    extends cityStats.colisactiv.plateform.project.stats.model.CampaignStats {}
interface BonusPrice extends city.colisactiv.project.model.BonusPrice {}

const useStyles = makeStyles(() => ({
  areasContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    align: 'left'
  },
  chip: {
    margin: 2,
    width: 'fit-content'
  },
  container: {
    '& .rdt_TableRow .rdt_TableCell:nth-child(2n)': {
      minWidth: '120px'
    },
    '& .rdt_TableCol:nth-child(2n)': {
      minWidth: '120px'
    },
    '& .rdt_TableRow .rdt_TableCell:nth-child(3n)': {
      minWidth: '120px'
    },
    '& .rdt_TableCol:nth-child(3n)': {
      minWidth: '120px'
    },
    '& .rdt_TableRow .rdt_TableCell:nth-child(4n)': {
      minWidth: '120px'
    },
    '& .rdt_TableCol:nth-child(4n)': {
      minWidth: '120px'
    },
    '& .rdt_TableRow .rdt_TableCell:nth-child(5n)': {
      minWidth: '120px'
    },
    '& .rdt_TableCol:nth-child(5n)': {
      minWidth: '120px'
    },
    '& .rdt_TableRow .rdt_TableCell:nth-child(6n)': {
      minWidth: '120px'
    },
    '& .rdt_TableCol:nth-child(6n)': {
      minWidth: '120px'
    },
    '& .rdt_TableRow .rdt_TableCell:nth-child(7n)': {
      minWidth: '150px'
    },
    '& .rdt_TableCol:nth-child(7n)': {
      minWidth: '150px'
    },
    '& .rdt_TableRow .rdt_TableCell:nth-child(8n)': {
      minWidth: '170px'
    },
    '& .rdt_TableCol:nth-child(8n)': {
      minWidth: '170px'
    },
    '& .rdt_TableRow .rdt_TableCell:nth-child(9n)': {
      minWidth: '150px'
    },
    '& .rdt_TableCol:nth-child(9n)': {
      minWidth: '150px'
    },
    '& .rdt_TableRow .rdt_TableCell:last-child': {
      minWidth: '80px',
      maxWidth: '80px'
    },
    '& .rdt_TableCol:last-child': {
      minWidth: '80px',
      maxWidth: '80px'
    },
    '& .rdt_TableRow .rdt_TableCell:first-child': {
      minWidth: '200px'
    },
    '& .rdt_TableCol:first-child': {
      minWidth: '200px'
    }
  }
}))

export interface CampaignRow {
  id: string
  name: string
  consumedBudget: number
  estimatedBudget: number
  potentialBudget: number
  missingBudget: number
  activeDeliveries: number
  totalBudget: number
  firstPercentage: number
  secondPercentage: number
  period: string
  status: StatusTagsFormProps
  actions: MenuItemAction[]
}

interface CampaignTableProps {
  campaigns: CampaignStats[]
  onRowClicked?: (row: CampaignRow) => void
  totalPages?: number
  page?: number
  handlePageChange?: (page: number) => void
  isLoading?: boolean
  getStatus: (row: CampaignStats) => StatusTagsFormProps
  getActions?: (row: CampaignStats) => MenuItemAction[]
}

export const CampaignTable = (props: CampaignTableProps) => {
  const {
    campaigns,
    onRowClicked,
    totalPages,
    handlePageChange,
    page,
    isLoading,
    getStatus,
    getActions = () => []
  } = props

  const { t } = useTranslation()
  const formatDate = (startDate: number, endDate: number): string => {
    return `Du ${UTCToTimedZone(
        startDate
    ).toLocaleDateString()} au ${UTCToTimedZone(endDate).toLocaleDateString()}`
  }

  const classes = useStyles()
  const theme = useContext(themeContext)
  const data: CampaignRow[] = campaigns.map(
      (campaign): CampaignRow => {
        const consumedBudget = Math.round(campaign.paidBonusAmount)
        const estimatedBudget = Math.round(campaign.totalBonusAmount)
        const missingBudget = Math.round(campaign.missingBudget)
        const totalBudget = Math.round(campaign.budget)
        return {
          id: campaign.id,
          name: campaign.details.name,
          activeDeliveries: campaign.activeDeliveryCount,
          consumedBudget: consumedBudget,
          estimatedBudget: estimatedBudget,
          missingBudget: missingBudget,
          totalBudget: totalBudget,
          firstPercentage: totalBudget !== 0 ? (consumedBudget * 100) / totalBudget : 100,
          secondPercentage: totalBudget !== 0 ? (estimatedBudget * 100) / totalBudget : 100,
          period: formatDate(
              campaign.details.period.startDate,
              campaign.details.period.endDate
          ),
          status: getStatus(campaign),
          actions: getActions(campaign)
        }
      }
  )

  const columns = [
    {
      name: 'Nom',
      cell: (row: CampaignRow) => (
          <PrimaryTypography
              data-tag='___react-data-table-allow-propagation___'
              align='left'
          >
            {row.name}
          </PrimaryTypography>
      )
    },
    {
      name: 'Budget total',
      cell: (row: CampaignRow) => (
          <PrimaryTypography
              data-tag='___react-data-table-allow-propagation___'
              align='left'
          >{`${displayNumber(row.totalBudget)} €`}</PrimaryTypography>
      )
    },
    {
      name: 'Budget payé',
      cell: (row: CampaignRow) => (
          <PrimaryTypography
              data-tag='___react-data-table-allow-propagation___'
              align='left'
          >
            {displayNumber(row.consumedBudget)} €
          </PrimaryTypography>
      )
    },
    {
      name: 'Budget engagé',
      cell: (row: CampaignRow) => (
          <PrimaryTypography
              data-tag='___react-data-table-allow-propagation___'
              align='left'
          >
            {displayNumber(row.estimatedBudget)} €
          </PrimaryTypography>
      )
    },
    {
      name: 'Budget manquant',
      cell: (row: CampaignRow) => (
          <Typography
              data-tag='___react-data-table-allow-propagation___'
              align='left'
              color={row.missingBudget > 0 ? 'error' : undefined}
          >
            {displayNumber(row.missingBudget)} €
          </Typography>
      )
    },
    {
      name: 'Livraisons actives',
      cell: (row: CampaignRow) => (
          <PrimaryTypography
              data-tag='___react-data-table-allow-propagation___'
              align='left'
          >
            {displayNumber(row.activeDeliveries)}
          </PrimaryTypography>
      )
    },
    {
      name: 'Période',
      cell: (row: CampaignRow) => (
          <PrimaryTypography
              data-tag='___react-data-table-allow-propagation___'
              align='left'
          >
            {row.period}
          </PrimaryTypography>
      )
    },
    {
      name: 'Progression',
      cell: (row: CampaignRow) => (
          <ProgressBar
              withLabel
              firstPercent={Math.round(row.firstPercentage)}
              firstColor={theme.secondaryColor}
              secondPercent={Math.round(row.secondPercentage)}
              secondColor={Math.round(row.estimatedBudget) <= Math.round(row.totalBudget) ? theme.primaryColor : "red"}
          />
      )
    },
    {
      name: 'Statut',
      cell: (row: CampaignRow) => {
        const status = row.status
        return <StatusTags {...status} />
      }
    },
    {
      name: 'Action',
      cell: (row: CampaignRow) => {
        const actions = row.actions
        return (
            <Box>
              <MoreOptions
                  handleClose={() => {}}
                  options={!!actions ? actions : []}
              />
            </Box>
        )
      }
    }
  ]
  return (
      <Table
          data={data}
          columns={columns}
          className={classes.container}
          onRowClicked={onRowClicked}
          handlePageChange={handlePageChange}
          totalPages={totalPages}
          page={page}
          isLoading={isLoading}
          keyField='id'
          noDataMessage={t('app_no_data_campaign_table')}
      />
  )
}
